// Plano de gravação para o TikTok. Cada pauta é um vídeo: gancho, roteiro e o
// recorte de produtos da vitrine que entram nele. Os produtos não são fixados
// por código: cada pauta declara categorias e palavras-chave, e a tela resolve
// isso contra o acervo real, então o plano continua válido quando entram
// compras novas.

export interface Pauta {
  id: string;
  semana: number;
  titulo: string;
  formato: string;
  duracao: string;
  gancho: string;
  roteiro: string[];
  gravacao: string;
  categorias?: string[];
  termos?: string[];
  minimoDeProdutos: number;
}

export const REGRAS_DO_CANAL = [
  "Nunca dizer quantas coisas você comprou. O número expõe volume de compra e faz a pessoa pensar em consumo compulsivo, não em curadoria. A posição é sempre a mesma: você testa e seleciona para que a outra pessoa não precise errar. Diga “testei um monte de coisa e estas passaram”, nunca “comprei noventa e três peças”.",
  "Toda peça que leva link de afiliada precisa dizer isso em voz alta e na legenda. Uma frase resolve: “os links são de afiliada, se você comprar por eles eu ganho uma comissãozinha, e não muda nada no seu preço”.",
  "Nunca dizer que usou o que ainda não chegou. O que está a caminho vira “acabei de comprar, vou testar e te conto”, nunca “eu recomendo”.",
  "Falar o código do produto em voz alta e deixar na tela. É por ele que a pessoa acha o item exato na vitrine em vez de desistir no meio.",
  "Um vídeo, um destino. O convite é sempre para o mesmo endereço da vitrine, sem mandar para dois lugares diferentes.",
  "Preço muda toda hora nessas lojas. Falar faixa (“saiu por menos de dez mil pesos”) em vez de valor exato evita vídeo que envelhece em uma semana.",
];

export const PAUTAS: Pauta[] = [
  {
    id: "p01",
    semana: 1,
    titulo: "Os seis achadinhos que sobreviveram ao meu teste",
    formato: "Curadoria",
    duracao: "45 a 60 segundos",
    gancho:
      "Testei um monte de coisa barata da internet. Estes seis são os que eu compraria de novo.",
    roteiro: [
      "Primeiro segundo: o item mais bonito da mão, já em uso, sem introdução falada.",
      "Um por um, seis no total. Para cada um: o que resolve na prática e o código na tela.",
      "Dizer em uma frase por que ele passou no teste, e citar de passagem que muita coisa não passou.",
      "Fechar com os seis juntos na mesa e o convite para a vitrine.",
    ],
    gravacao:
      "Grave em uma tomada só, com a câmera parada e apoiada. O corte rápido é feito depois; gravar já cortando faz você perder reação boa.",
    minimoDeProdutos: 6,
  },
  {
    id: "p02",
    semana: 1,
    titulo: "Cinco achadinhos de papelaria por menos do que um café",
    formato: "Lista rápida",
    duracao: "30 a 40 segundos",
    gancho: "Cinco coisas de papelaria que eu comprei e que me fizeram voltar a escrever à mão.",
    roteiro: [
      "Abrir com o item mais bonito da mão, sem falar nada, e só depois começar.",
      "Um item por vez, cada um com o que ele resolve na prática.",
      "Deixar o mais barato para o final e dizer o preço dele em voz alta.",
      "Fechar com a mesa montada e o convite para a vitrine.",
    ],
    gravacao:
      "Luz de janela e fundo liso. Papelaria some em fundo bagunçado; a mesa precisa estar vazia antes de começar.",
    categorias: ["Papelaria"],
    minimoDeProdutos: 5,
  },
  {
    id: "p03",
    semana: 1,
    titulo: "Roupa de internet: o que veio como na foto e o que não veio",
    formato: "Prova de roupa",
    duracao: "40 a 60 segundos",
    gancho:
      "Roupa barata da internet engana na foto. Provei as peças para te dizer quais valem.",
    roteiro: [
      "Começar por uma peça que decepcionou e falar isso sem suavizar. Sinceridade é o que segura o vídeo.",
      "Depois, três peças que ficaram boas, com o corte na transição de roupa.",
      "Dizer o tamanho que você pediu e se veio no tamanho certo. É a dúvida número um de quem compra roupa por foto.",
      "Fechar dizendo que os códigos estão na vitrine, separados por peça.",
    ],
    gravacao:
      "Marque um ponto no chão com fita para ficar sempre no mesmo lugar. Sem isso, cada troca de roupa desloca você do enquadramento e a transição não fecha.",
    categorias: ["Roupas"],
    minimoDeProdutos: 6,
  },
  {
    id: "p04",
    semana: 2,
    titulo: "Transformando a mesa bagunçada em setup de gravação",
    formato: "Antes e depois",
    duracao: "30 a 45 segundos",
    gancho: "Minha mesa estava assim. Em dez minutos ficou assim.",
    roteiro: [
      "Mostrar o antes de verdade, com fio à mostra e bagunça real. Antes limpo demais mata o depois.",
      "Sequência acelerada de você colocando cada organizador no lugar.",
      "Parar em cada item que resolveu um problema específico: o fio que sumiu, a caneta que ficou em pé.",
      "Revelar o depois com a mesma câmera e o mesmo enquadramento do começo.",
    ],
    gravacao:
      "Antes e depois só funciona se a câmera não sair do lugar entre os dois. Tripé fixo, e não encoste nele.",
    categorias: ["Organização", "Tecnologia"],
    minimoDeProdutos: 5,
  },
  {
    id: "p05",
    semana: 2,
    titulo: "Testando os produtos de beleza mais duvidosos que comprei",
    formato: "Teste ao vivo",
    duracao: "45 a 60 segundos",
    gancho: "Comprei uma máscara de LED de sete cores. Vou usar na frente de vocês e falar se presta.",
    roteiro: [
      "Colocar o produto mais estranho logo no primeiro segundo, sem explicar o que é.",
      "Explicar o que ele promete enquanto usa.",
      "Dar o veredito honesto, inclusive se for negativo. Um veredito negativo por vídeo dá credibilidade a todos os outros.",
      "Listar rapidamente os outros dois ou três de beleza que valeram a pena.",
    ],
    gravacao:
      "Grave o rosto com luz frontal, nunca contra a janela. Em vídeo de beleza, se a pele não aparece direito, o teste não convence.",
    categorias: ["Beleza"],
    minimoDeProdutos: 4,
  },
  {
    id: "p06",
    semana: 2,
    titulo: "O que eu levo na mala e cabe na bagagem de mão",
    formato: "Lista de viagem",
    duracao: "40 a 50 segundos",
    gancho: "Isso tudo cabe na bagagem de mão e me economiza briga no aeroporto.",
    roteiro: [
      "Abrir com a mala aberta e cheia, e a frase de que tudo ali vai passar no raio-x.",
      "Item por item, cada um com o motivo prático de existir: o que pesa, o que vaza, o que some.",
      "Destacar a balança de bagagem e o organizador de mala, que são os que mais resolvem problema.",
      "Fechar com a mala fechada e o peso final na tela.",
    ],
    gravacao:
      "Filme de cima, com a mala no chão e a câmera acima. É o único ângulo em que dá para ver tudo ao mesmo tempo.",
    categorias: ["Viagem", "Bolsas e acessórios"],
    minimoDeProdutos: 5,
  },
  {
    id: "p07",
    semana: 3,
    titulo: "Montando um look completo com peças de menos de dez mil pesos",
    formato: "Vestindo o look",
    duracao: "30 a 40 segundos",
    gancho: "Look inteiro, dos brincos ao sapato, por menos do que um jantar.",
    roteiro: [
      "Começar já vestida com a peça principal e ir somando acessório na frente da câmera.",
      "Dizer o preço aproximado de cada item conforme coloca.",
      "Somar tudo em voz alta no final.",
      "Convite para a vitrine com o código do conjunto.",
    ],
    gravacao:
      "Espelho de corpo inteiro ou tripé afastado. Corte a cada peça nova para o vídeo ter ritmo.",
    categorias: ["Roupas", "Bolsas e acessórios", "Cabelo"],
    minimoDeProdutos: 5,
  },
  {
    id: "p08",
    semana: 3,
    titulo: "Decoração de viagem: transformando a parede em aeroporto",
    formato: "Montagem de ambiente",
    duracao: "40 a 60 segundos",
    gancho: "Comprei uma parede inteira de aeroporto por menos de cinquenta mil pesos.",
    roteiro: [
      "Parede vazia no primeiro segundo.",
      "Colocar quadro, adesivo de janela de avião e escultura, um por vez, em tempo acelerado.",
      "Acender a luminária de avião no escuro para o efeito final.",
      "Revelar a parede pronta com a luz baixa.",
    ],
    gravacao:
      "A última cena precisa ser de noite, com a luminária acesa. Grave o resto de dia e deixe só o final para depois do pôr do sol.",
    categorias: ["Casa e decoração"],
    termos: ["avião", "viagem", "mapa", "aeroporto", "voo"],
    minimoDeProdutos: 5,
  },
  {
    id: "p09",
    semana: 3,
    titulo: "Coisas de cozinha que eu duvidei e hoje uso toda semana",
    formato: "Lista rápida",
    duracao: "30 a 45 segundos",
    gancho: "Achei que fosse bobagem. Hoje uso toda semana.",
    roteiro: [
      "Usar o item mais visual logo de cara, em funcionamento.",
      "Passar por três ou quatro itens, sempre mostrando funcionando, nunca parado na mão.",
      "Dizer qual deles foi arrependimento, se houver.",
      "Fechar com a bancada montada.",
    ],
    gravacao:
      "Grave em cima da bancada, com a câmera baixa e próxima. Produto de cozinha só convence em uso, não em cima da mesa.",
    categorias: ["Cozinha"],
    minimoDeProdutos: 4,
  },
  {
    id: "p10",
    semana: 4,
    titulo: "Comprei para os pets e testei nos meus",
    formato: "Teste com pet",
    duracao: "30 a 45 segundos",
    gancho: "Comprei uma caminha de sofá para eles e a reação foi essa.",
    roteiro: [
      "Reação do pet no primeiro segundo, sem contexto nenhum.",
      "Só depois explicar o que é e quanto custou.",
      "Mostrar os outros itens de pet em uso rápido.",
      "Fechar com o pet usando o produto principal.",
    ],
    gravacao:
      "Deixe a câmera gravando muito mais tempo do que você acha que precisa. Com bicho, o melhor momento acontece quando você já desistiu.",
    categorias: ["Pets"],
    minimoDeProdutos: 3,
  },
  {
    id: "p11",
    semana: 4,
    titulo: "Cinco coisas que eu testei e não recomendo",
    formato: "Ranking honesto",
    duracao: "40 a 60 segundos",
    gancho: "Cinco coisas que eu testei para você não precisar testar.",
    roteiro: [
      "Anunciar que este vídeo é o contrário do de sempre: aqui é o que não vale.",
      "Cinco itens, do menos ruim ao pior, com o motivo concreto de cada um.",
      "Fechar dizendo qual você usaria de novo sem pensar.",
      "Convite para a vitrine, deixando claro que lá só entra o que passou no teste.",
    ],
    gravacao:
      "Este vídeo é falado, então o áudio manda. Grave em ambiente com tecido por perto, longe de parede nua e de cozinha vazia.",
    minimoDeProdutos: 5,
  },
  {
    id: "p12",
    semana: 4,
    titulo: "Respondendo: onde você comprou isso?",
    formato: "Resposta a comentário",
    duracao: "20 a 30 segundos",
    gancho: "Perguntaram onde eu comprei isso. Resposta rápida.",
    roteiro: [
      "Abrir com o produto mais perguntado nos comentários dos vídeos anteriores.",
      "Nome, código e loja, direto.",
      "Mostrar mais dois parecidos que você tem.",
      "Convite curto para a vitrine.",
    ],
    gravacao:
      "Formato mais barato de todos e o que mais converte, porque responde uma dúvida que alguém teve de verdade. Guarde para gravar junto com outro vídeo, no mesmo dia e na mesma roupa.",
    minimoDeProdutos: 3,
  },
];
